import {
    FormCheckbox,
    FormInput,
    FormInputPassword,
    FormTextArea,
    type GroupedOptions,
    type SelectOption
} from '@auviknetworks/iceberg-ui';
import { FormFieldsGroup } from 'application/Shared/Components/Form/FormFieldsGroup';

import { type FieldSchemaJson, type StaticOptions } from './Field.type';
import { FormKeyValue } from './FormKeyValue';
import { FormSelectHandler } from './FormSelectHandler';
import { type Fields } from './types';

/**
 * A mapping of field types used by the Fields Factory. In this variant showing only the form summary.
 * All fields are set to read-only.
 */
export const fields: Fields = {
    checkbox: {
        Component: FormCheckbox,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked as
                        | boolean
                        | undefined,
                    helperText: schema.control!.helperText,
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                defaultChecked: false,
                helperText: schema.hint,
                label: t(schema.label || schema.name),
                name: schema.name,
                required: false
            };
        }
    },
    date: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'date',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'date',
                label: t(schema.label || schema.name),
                name: schema.name,
                required: false
            };
        }
    },
    date_time: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'datetime-local',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'datetime-local',
                label: t(schema.label || schema.name),
                name: schema.name,
                required: false
            };
        }
    },
    email: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'email',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$',
                    placeholder: 'e.g., user@example.com',
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'email',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$',
                placeholder: 'e.g., user@example.com',
                required: false
            };
        }
    },
    field_group: {
        Component: FormFieldsGroup,
        Transformer: (schema, t) => ({
            children: schema.properties as FieldSchemaJson[],
            disabled: schema.control!.disabled as boolean | undefined,
            helperText: schema.control!.helperText,
            label: t(schema.label || schema.name),
            name: schema.name,
            readOnly: true,
            required: schema.optional !== true,
            variant: 'success'
        })
    },
    integer: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'number',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: 'd*',
                    readOnly: true,
                    required: false,
                    // This is setting the step to full numbers only
                    step: 1
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'number',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: 'd*',
                required: false,
                // This is setting the step to full numbers only
                step: 1
            };
        }
    },
    key_value: {
        Component: FormKeyValue,
        Transformer: (schema) => ({
            label: schema.label || schema.name,
            name: schema.name,
            placeholder: 'schema.empty_list_title',
            readOnly: true,
            required: false
        })
    },
    number: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'number',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'number',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: 'd*',
                required: false
            };
        }
    },
    password: {
        Component: FormInputPassword,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                label: t(schema.label || schema.name),
                name: schema.name,
                required: false
            };
        }
    },
    phone: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'tel',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    placeholder: 'e.g., +1 (123) 456 7890',
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'tel',
                label: t(schema.label || schema.name),
                name: schema.name,
                placeholder: 'e.g., +1 (123) 456 7890',
                required: false
            };
        }
    },
    select: {
        Component: FormSelectHandler,
        Transformer: (schema, t) => {
            const baseProps = {
                isClearable: false,
                label: t(schema.label || schema.name),
                name: schema.name,
                readOnly: true,
                required: false
            };
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                const additionalProps = {
                    ...baseProps,
                    defaultChecked: schema.control!.defaultChecked,
                    helperText: schema.control!.helperText,
                    isMulti: !!schema.control!.allowMultipleSelect as
                        | boolean
                        | undefined
                };

                // @ts-expect-error fix type
                if (schema.control!.options.type === 'api') {
                    // @ts-expect-error fix type
                    const { operation } = schema.control!.options;

                    return {
                        ...additionalProps,
                        operation
                    };
                }

                return {
                    ...additionalProps,
                    options: (
                        schema.control!.options! as StaticOptions
                    ).values.map((option: unknown) => option) as (
                        | SelectOption
                        | GroupedOptions
                    )[]
                };
            }

            return {
                ...baseProps,
                options: schema.pick_list!.map(([value, label]) => ({
                    label,
                    value
                }))
            };
        }
    },
    subdomain: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'text',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: '^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$',
                    placeholder: 'e.g., mysubdomain',
                    readOnly: true,
                    required: false,
                    suffix: '.example.com'
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'text',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: '^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$',
                placeholder: 'e.g., mysubdomain',
                required: false,
                suffix: '.example.com'
            };
        }
    },
    text: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'text',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'text',
                label: t(schema.label || schema.name),
                name: schema.name,
                required: false
            };
        }
    },
    'text-area': {
        Component: FormTextArea,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: true,
                    required: false
                };
            }

            return {
                helperText: schema.hint,
                label: t(schema.label || schema.name),
                name: schema.name,
                required: false
            };
        }
    },
    url: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    helperText: schema.control!.helperText,
                    inputType: 'url',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: 'https://.*',
                    placeholder: 'e.g., https://example.com',
                    readOnly: true,
                    required: false,
                    suffix: '.example.com'
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'url',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: 'https://.*',
                placeholder: 'e.g., https://example.com',
                required: false,
                suffix: '.example.com'
            };
        }
    }
};
